import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { 
  FaPaperPlane, 
  FaRocket, 
  FaCode, 
  FaServer, 
  FaMobile, 
  FaShieldAlt,
  FaUsers,
  FaLightbulb,
  FaGraduationCap,
  FaUpload
} from 'react-icons/fa';
import { apiService } from '../services/api';

const Hire = () => {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const requestTypes = [
    'Hire Me',
    'Freelance Project',
    'Startup Partnership',
    'Internship Opportunity',
    'Remote Job',
    'Contract',
    'Mentorship',
    'Speaking Invitation'
  ];

  const priorities = ['Low', 'Normal', 'High', 'Urgent'];

  const options = [
    {
      icon: FaRocket,
      title: 'Full Stack Development',
      description: 'Complete web applications from design to deployment'
    },
    {
      icon: FaCode,
      title: 'Frontend Development',
      description: 'Responsive, interactive user interfaces'
    },
    {
      icon: FaServer,
      title: 'Backend Development',
      description: 'Scalable server-side applications and APIs'
    },
    {
      icon: FaShieldAlt,
      title: 'Blockchain Development',
      description: 'Decentralized applications and smart contracts'
    },
    {
      icon: FaMobile,
      title: 'Embedded Systems',
      description: 'Software for embedded devices and IoT'
    },
    {
      icon: FaLightbulb,
      title: 'Technical Consulting',
      description: 'Expert guidance on technology and architecture'
    },
    {
      icon: FaUsers,
      title: 'Mentorship',
      description: 'One-on-one mentoring for developers'
    },
    {
      icon: FaGraduationCap,
      title: 'Final Year Project Guidance',
      description: 'Help with academic projects and research'
    }
  ];

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await apiService.submitHireRequest(data);
      toast.success('Hire request submitted successfully! I\'ll get back to you within 24 hours.');
      reset();
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Hire Me
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            Let's Work <span className="gradient-text">Together</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Choose the engagement that fits your needs and let's build something amazing
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Submit Your Request</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      {...register('full_name', { required: 'Full name is required' })}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="John Doe"
                    />
                    {errors.full_name && (
                      <p className="text-red-400 text-sm mt-1">{errors.full_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email format'
                        }
                      })}
                      type="email"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      {...register('company')}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Country
                    </label>
                    <input
                      {...register('country')}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Your country"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Request Type *
                  </label>
                  <select
                    {...register('request_type', { required: 'Request type is required' })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select request type</option>
                    {requestTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.request_type && (
                    <p className="text-red-400 text-sm mt-1">{errors.request_type.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Budget Range
                    </label>
                    <input
                      {...register('budget')}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., $1,000 - $5,000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Timeline
                    </label>
                    <input
                      {...register('timeline')}
                      type="text"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., 2 weeks"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Priority
                  </label>
                  <select
                    {...register('priority')}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {priorities.map((priority) => (
                      <option key={priority} value={priority.toLowerCase()}>{priority}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Requirements *
                  </label>
                  <textarea
                    {...register('requirements', { required: 'Requirements are required' })}
                    rows="4"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe your project requirements, goals, and expectations..."
                  />
                  {errors.requirements && (
                    <p className="text-red-400 text-sm mt-1">{errors.requirements.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Meeting Preference
                  </label>
                  <select
                    {...register('meeting_preference')}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="video">Video Call</option>
                    <option value="audio">Audio Call</option>
                    <option value="in-person">In Person</option>
                    <option value="email">Email Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Attachments
                  </label>
                  <div className="flex items-center justify-center w-full">
                    <label className="w-full flex flex-col items-center px-4 py-6 bg-white/5 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:bg-white/10 transition-all duration-300">
                      <FaUpload className="text-3xl text-gray-500 mb-2" />
                      <span className="text-sm text-gray-400">Drop files here or click to upload</span>
                      <span className="text-xs text-gray-500">PDF, DOC, DOCX, ZIP (Max 10MB)</span>
                      <input
                        {...register('attachments')}
                        type="file"
                        className="hidden"
                        multiple
                      />
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : (
                    <>
                      Submit Request
                      <FaPaperPlane />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>

          <div className="lg:col-span-2">
            <div className="glass rounded-2xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-4">Quick Info</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <span className="text-gray-300">Available for remote work</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-400">Response time:</span>
                  <span className="text-white font-medium">&lt; 24 hours</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-400">Timezone:</span>
                  <span className="text-white font-medium">GMT+1</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-400">Location:</span>
                  <span className="text-white font-medium">Kaduna, Nigeria</span>
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Services</h2>
              <div className="space-y-3">
                {options.map((option, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="flex items-start gap-3">
                      <option.icon className="text-primary-400 mt-0.5" />
                      <div>
                        <h4 className="text-white font-medium text-sm">{option.title}</h4>
                        <p className="text-gray-400 text-xs">{option.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hire;